<template>
  <div class="atlas-dashboard-widget">
    <!-- Standard page header -->
    <div class="standard-page-header standard-page-header--no-bottom-line atlas-dashboard-widget__header">
      <h2 class="global-h2 standard-page-header__title">
        <RouterLink
          :to="{
            name: 'CalendarPosts',
            params: { customerId: props.customerId },
          }"
        >
          Atlas Social
        </RouterLink>
      </h2>

      <div class="standard-page-header__subtitle-and-link">
        <div class="standard-page-header__subtitle">
          Connect your destination's unique interests with the stories, creators, and content already flowing through Shrpa — helping you schedule and share with ease.
        </div>
      </div>
    </div>
    <!-- / Standard page header -->

    <Loader v-if="isLoadingDashboardData" />

    <div v-else-if="dashboardData?.postsPendingApproval?.length" class="atlas-dashboard-widget__posts-list">
      <SocialPostPreview
        class="atlas-dashboard-widget__post"
        v-for="socialPost in dashboardData.postsPendingApproval.slice(0, howManyPostsToShow)"
        :key="socialPost?.id"
        :assetIds="socialPost.assetIds"
        :caption="socialPost.caption || ''"
        :onlyAtlasReadonlyFieldsVisible="true"
        :isWithTextarea="false"
        :isWithShareAndDislikeButtons="false"
        :fancyboxKey="socialPost?.id"
        :post="socialPost"
        @addSocialAccount="linkMoreAccounts"
      />
    </div>

    <div v-else-if="dashboardData" class="atlas-dashboard-widget__status-section">
      <div class="atlas-dashboard-widget__status-text">
        <div v-if="dashboardData.nextPostScheduledOn" class="atlas-dashboard-widget__status-count">
          Your next post is scheduled for {{ DayjsExtended(dashboardData.nextPostScheduledOn).format("dddd [the] Do") }}
        </div>
        <div v-if="dashboardData.nextPostGenForWeek" class="atlas-dashboard-widget__status-subtitle">
          <span>{{
            `${dashboardData.postsPerWeek * dashboardData.weeksToGenPosts} new posts will be created on ${DayjsExtended(dashboardData.nextPostGenOnWeek).format("dddd [the] Do")} for the ${dashboardData.weeksToGenPosts} week period starting ${DayjsExtended(dashboardData.nextPostGenForWeek).format("MMM Do")}`
          }}</span>
          <LinkWithIcon class="atlas-dashboard-widget__config-link" tag="router-link" isDottedUnderline color="gray" :to="{ name: 'CalendarConfig', params: { customerId: props.customerId } }">
            <template #icon><IconEmbedded name="gear_2" :size="18" /></template>
          </LinkWithIcon>
        </div>
      </div>
    </div>

    <SrpButton
      :isDisabled="props.isCustomerDisabled"
      tag="RouterLink"
      size="small"
      :fill="dashboardData?.postsPendingApproval?.length ? 'solid' : 'outlined'"
      class="atlas-dashboard-widget__generate-post-button"
      :to="{
        name: 'CalendarPosts',
        params: { customerId: props.customerId },
        hash: `#reviewAndApprove=true`,
      }"
    >
      {{ dashboardData?.postsPendingApproval?.length ? "Review & Approve" : "View Content Calendar" }}
    </SrpButton>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from "vue";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import ContentCalendarHelper from "@logic/ContentCalendarHelper";
import DayjsExtended from "@logic/DayjsExtended";

// Components
import SrpButton from "@components/ui/SrpButton.vue";
import Loader from "@components/Loader/Loader.vue";
import SocialPostPreview from "@components/SocialPostPreview/index.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

// Types
import { CalendarDashboardSummaryResult } from "@contracts/contentCalendar";

const props = withDefaults(
  defineProps<{
    customerId: string;
    isCustomerDisabled: boolean;
  }>(),
  {
    customerId: "",
    isCustomerDisabled: false,
  }
);

// How many posts to show =====================================================
const howManyPostsToShow = 3;

// Load dashboard data ========================================================
const isLoadingDashboardData = ref<boolean>(true);
const dashboardData = ref<CalendarDashboardSummaryResult | null>(null);

async function loadDashboardData() {
  isLoadingDashboardData.value = true;
  dashboardData.value = await ContentCalendarRepo.getDashboardSummary(props.customerId);
  isLoadingDashboardData.value = false;
}

// Link more social accounts ===================================================
async function linkMoreAccounts() {
  await ContentCalendarHelper.linkMoreAccounts(props.customerId, "dashboard");
}

onMounted(loadDashboardData);

watch(() => props.customerId, loadDashboardData);
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Atlas dashboard widget ======================================================
.atlas-dashboard-widget {
  &__header {
    margin-bottom: 25px;
  }

  &__posts-list {
    gap: $desktop-wide-grid-gap-width;
    margin-bottom: 20px;
    display: flex;
    flex-wrap: nowrap;
    overflow: hidden;
  }

  &__post {
    width: calc(33.3% - $desktop-wide-grid-gap-width * 2 / 3);
    flex-shrink: 0;
  }

  &__status-section {
    margin-bottom: 20px;
  }

  &__status-text {
  }

  &__status-count {
    font-size: 17px;
    font-weight: 600;
    margin-bottom: 8px;
    color: #333;
  }

  &__status-subtitle {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #666;
  }

  &__config-link {
    flex-shrink: 0;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .atlas-dashboard-widget {
    &__posts-list {
      gap: $laptop-grid-gap-width;
    }

    &__post {
      width: calc(33.3% - $laptop-grid-gap-width * 2 / 3);
    }
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .atlas-dashboard-widget {
    &__posts-list {
      gap: $tablet-large-grid-gap-width;
    }

    &__post {
      width: calc(33.3% - $tablet-large-grid-gap-width * 2 / 3);
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .atlas-dashboard-widget {
    &__posts-list {
      gap: $tablet-grid-gap-width;
    }

    &__post {
      width: calc(50% - $tablet-grid-gap-width * 1 / 2);
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .atlas-dashboard-widget {
    &__post {
      width: 100%;
    }
  }
}
</style>
